import React, { useMemo, useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import MultiTrendChart from "../../Components/Charts/MultiTrendChart";
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  DatabaseIcon,
  DownloadIcon,
  EyeIcon,
  FilterIcon,
  InfoIcon,
  MoreIcon,
  SearchIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SyncIcon,
} from "../../Components/Icons";
import {
  ISSUE_BREAKDOWN,
  ISSUE_FILTERS,
  LAST_UPDATED,
  RECENT_ISSUES,
  RULE_VIOLATIONS,
  SUMMARY_CARDS,
  TOTAL_ISSUES,
  TOTAL_ISSUE_ROWS,
  TREND_MAX,
  TREND_MONTHS,
  TREND_SERIES,
} from "./data";
import "./DataQuality.css";

const ICONS = {
  shield: ShieldCheckIcon,
  database: DatabaseIcon,
  check: CheckCircleIcon,
  alert: AlertTriangleIcon,
  rules: SettingsIcon,
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const EMPTY_FILTERS = ISSUE_FILTERS.reduce(
  (acc, filter) => ({ ...acc, [filter.key]: "" }),
  {}
);

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/\s+/g, "-")}`;

// Short month labels fit the axis; the tooltip shows the full "Mon YYYY".
const AXIS_LABELS = TREND_MONTHS.map((month) => month.split(" ")[0]);

function CardHeader({ title, action }) {
  return (
    <div className="dgi-card__header">
      <h2 className="dgi-card__title">
        {title}
        <span className="dgi-card__info" title={`About ${title}`}>
          <InfoIcon size={15} />
        </span>
      </h2>
      {action ?? (
        <button
          type="button"
          className="dgi-card__more"
          aria-label="More options"
        >
          <MoreIcon size={18} />
        </button>
      )}
    </div>
  );
}

export default function DataQuality() {
  const [granularity, setGranularity] = useState("Monthly");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);

  const setFilter = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setPage(1);
  };

  const filtersActive =
    search.trim() !== "" || Object.values(filters).some(Boolean);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return RECENT_ISSUES.filter((issue) => {
      if (term && !`${issue.name} ${issue.nif}`.toLowerCase().includes(term)) {
        return false;
      }
      if (filters.type && issue.type !== filters.type) return false;
      if (filters.severity && issue.severity !== filters.severity) return false;
      if (filters.source && issue.source !== filters.source) return false;
      if (filters.status && issue.status !== filters.status) return false;
      return true;
    });
  }, [search, filters]);

  const totalPages = Math.ceil(TOTAL_ISSUE_ROWS / perPage);
  const allSelected = rows.length > 0 && selected.length === rows.length;

  const toggleAll = () =>
    setSelected(allSelected ? [] : rows.map((row) => row.id));

  const toggleRow = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Data Quality Overview</h1>
          <p className="page__subtitle">
            Monitor, analyze and improve the quality of taxpayer data across all
            integrated sources.
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <CalendarIcon size={17} />
            01 Jan 2026 - 01 Sep 2026
            <ChevronDownIcon size={16} />
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <DownloadIcon size={17} />
            Export Report
          </button>
        </div>
      </div>

      <p className="page__updated">
        Last updated: {LAST_UPDATED}
        <button type="button" className="link page__refresh">
          <SyncIcon size={15} />
          Refresh Data
        </button>
      </p>

      {/* ------------------------------------------------------ summary -- */}
      <section className="row g-3" aria-label="Data quality summary">
        {SUMMARY_CARDS.map((card) => {
          const Icon = ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl" key={card.key}>
              <article className="dq-card h-100">
                <span className={`kpi__icon kpi__icon--${card.tone}`}>
                  <Icon size={22} />
                </span>
                <div className="dq-card__body">
                  <p className="kpi__label">{card.label}</p>
                  <p className="dq-card__value">{card.value}</p>
                  {card.delta && (
                    <p className="kpi__delta dq-card__delta">
                      <ArrowUpRightIcon size={13} />
                      {card.delta}
                    </p>
                  )}
                  {card.foot && <p className="kpi__foot">{card.foot}</p>}
                  {card.progress != null && (
                    <span className="score__track dq-card__track">
                      <span
                        className="score__fill score__fill--good"
                        style={{ width: `${card.progress}%` }}
                      />
                    </span>
                  )}
                </div>
              </article>
            </div>
          );
        })}
      </section>

      {/* ------------------------------------------------------- charts -- */}
      <div className="dq-charts">
        <div>
          <article className="dgi-card h-100">
            <CardHeader title="Data Quality Summary" />
            <DonutChart
              segments={ISSUE_BREAKDOWN}
              total={TOTAL_ISSUES.toLocaleString("en-US")}
              totalLabel="Total Issues"
            />
            <button type="button" className="dgi-card__link">
              View Data Quality Report <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>

        <div>
          <article className="dgi-card h-100">
            <CardHeader
              title="Issues Trend"
              action={
                <label className="select">
                  <select
                    value={granularity}
                    onChange={(event) => setGranularity(event.target.value)}
                    aria-label="Trend granularity"
                  >
                    <option>Monthly</option>
                    <option>Quarterly</option>
                    <option>Yearly</option>
                  </select>
                  <ChevronDownIcon size={16} />
                </label>
              }
            />
            <MultiTrendChart
              labels={AXIS_LABELS}
              series={TREND_SERIES}
              max={TREND_MAX}
            />
          </article>
        </div>

        <div>
          <article className="dgi-card h-100">
            <CardHeader title="Top Data Quality Rules Violations" />
            <div className="dgi-table-wrap">
              <table className="dgi-table dgi-table--compact">
                <thead>
                  <tr>
                    <th>Rule Name</th>
                    <th>Affected Records</th>
                    <th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {RULE_VIOLATIONS.map((row) => (
                    <tr key={row.rule}>
                      <td>{row.rule}</td>
                      <td>{row.records}</td>
                      <td>
                        <span className={chipClass(row.severity)}>
                          {row.severity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="dgi-card__link">
              View All Rules <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>
      </div>

      {/* ------------------------------------------------ recent issues -- */}
      <section className="dgi-card">
        <CardHeader title="Recent Data Quality Issues" action={<span />} />

        <div className="dq-filters">
          <div className="filters__search">
            <input
              type="search"
              placeholder="Search by taxpayer name or NIF..."
              aria-label="Search issues"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            <SearchIcon size={17} />
          </div>

          {ISSUE_FILTERS.map((filter) => (
            <span className="select select--block" key={filter.key}>
              <select
                value={filters[filter.key]}
                aria-label={filter.placeholder}
                onChange={setFilter(filter.key)}
              >
                <option value="">{filter.placeholder}</option>
                {filter.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>
          ))}

          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <FilterIcon size={16} />
            Filters
          </button>
          <button
            type="button"
            className="filters__clear"
            onClick={clearFilters}
            disabled={!filtersActive}
          >
            <SyncIcon size={16} />
            Clear Filters
          </button>
        </div>

        <div className="dgi-table-wrap">
          <table className="dgi-table dgi-table--issues">
            <thead>
              <tr>
                <th className="dgi-table__check">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all issues"
                  />
                </th>
                <th>Taxpayer Name</th>
                <th>NIF</th>
                <th>Issue Type</th>
                <th>Issue Description</th>
                <th>Source System</th>
                <th>Detected On</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((issue) => (
                <tr key={issue.id}>
                  <td className="dgi-table__check">
                    <input
                      type="checkbox"
                      checked={selected.includes(issue.id)}
                      onChange={() => toggleRow(issue.id)}
                      aria-label={`Select issue for ${issue.name}`}
                    />
                  </td>
                  <td>
                    <button type="button" className="link">
                      {issue.name}
                    </button>
                  </td>
                  <td>{issue.nif}</td>
                  <td>{issue.type}</td>
                  <td className="dq-desc">{issue.description}</td>
                  <td>{issue.source}</td>
                  <td>{issue.detected}</td>
                  <td>
                    <span className={chipClass(issue.severity)}>
                      {issue.severity}
                    </span>
                  </td>
                  <td>
                    <span className={chipClass(issue.status)}>
                      {issue.status}
                    </span>
                  </td>
                  <td>
                    <span className="row-actions">
                      <button
                        type="button"
                        className="row-actions__btn"
                        aria-label={`View issue for ${issue.name}`}
                      >
                        <EyeIcon size={17} />
                      </button>
                      <button
                        type="button"
                        className="dgi-card__more"
                        aria-label={`Actions for ${issue.name}`}
                      >
                        <MoreIcon size={18} />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <p className="dgi-table__empty">
            No issues match these filters.{" "}
            <button type="button" className="link" onClick={clearFilters}>
              Clear filters
            </button>
          </p>
        )}

        <div className="listbar listbar--foot">
          <p className="listbar__count">
            Showing {rows.length === 0 ? 0 : 1} to {rows.length} of{" "}
            {TOTAL_ISSUE_ROWS.toLocaleString("en-US")} issues
          </p>

          <div className="listbar__pager">
            <span className="select">
              <select
                value={perPage}
                aria-label="Rows per page"
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} per page
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>

            <nav className="pager" aria-label="Pagination">
              <button
                type="button"
                className="pager__btn"
                aria-label="First page"
                disabled={page === 1}
                onClick={() => setPage(1)}
              >
                <ChevronsLeftIcon size={16} />
              </button>
              <button
                type="button"
                className="pager__btn"
                aria-label="Previous page"
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              >
                <ChevronLeftIcon size={16} />
              </button>

              {[1, 2, 3, 4, 5].map((entry) => (
                <button
                  key={entry}
                  type="button"
                  className={`pager__btn${
                    entry === page ? " pager__btn--active" : ""
                  }`}
                  aria-current={entry === page ? "page" : undefined}
                  onClick={() => setPage(entry)}
                >
                  {entry}
                </button>
              ))}
              <span className="pager__gap">…</span>
              <button
                type="button"
                className="pager__btn"
                onClick={() => setPage(totalPages)}
              >
                {totalPages.toLocaleString("en-US")}
              </button>

              <button
                type="button"
                className="pager__btn"
                aria-label="Next page"
                disabled={page === totalPages}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              >
                <ChevronRightIcon size={16} />
              </button>
              <button
                type="button"
                className="pager__btn"
                aria-label="Last page"
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
              >
                <ChevronsRightIcon size={16} />
              </button>
            </nav>
          </div>
        </div>
      </section>
    </div>
  );
}
